// React
import { useEffect, useRef } from "react";

// External libraries
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Internal
import { DatePicker } from "../datepicker/datepicker";
import { getDataTestId } from "../functions/data-test-ids";
import { InputGuid } from "../inputs/input-guid";
import { InputNumber } from "../inputs/input-number";
import { InputString } from "../inputs/input-string";
import { InputText } from "../inputs/input-text";
import { InputTime } from "../inputs/input-time";
import { InputTimeSpan } from "../inputs/input-timespan";
import { Select } from "../select/select";
import { TagControl } from "../tag-control/tag-control";
import { DropdownAlignment, DropdownDirection, DropdownPanel, DropdownPosition, useDropdownPanel } from "../dropdown-panel/dropdown-panel";

// CSS
import "./multi-value-type-input.css";

const MultiValueInputTypes = {
    String: 1,
    Select: 2,
    Date: 3,
    Number: 4,
    Time: 5,
    Guid: 6,
    TimeSpan: 7,
    Strings: 8,
    Numbers: 9,
    Text: 10,
    None: 100
};

function ValueTypeMenuItem(props) {
    const clickHandler = () => {
        props.OnClick(props.ValueType);
    };

    return (
        <div className="eone-ui-multi-value-type-input-menu-item" onClick={clickHandler}>
            <FontAwesomeIcon fixedWidth={true} icon={props.Icon} />
            <label className="eone-ui-multi-value-type-input-menu-item-label">{props.Label}</label>
        </div>
    )
}

function ValueTypeMenu(props) {
    const changeValueType = (valueType) => {
        props.ChangeValueType(valueType);
    };

    const getMenuItems = () => {
        return props.ValueTypes.map((valueType, index) => (
            <ValueTypeMenuItem
                key={index}
                ValueType={valueType.ValueType}
                Label={valueType.Label}
                Icon={valueType.Icon}
                OnClick={changeValueType} />
        ));
    };

    if (!props.Open) {
        return null;
    }

    return (
        <div className="eone-ui-multi-value-type-input-menu">
            {getMenuItems()}
        </div>
    )
}

function ValueTypeMenuButton(props) {
    const elementRef = useRef(null);

    const {
        open,
        setOpen,
        close,
        triggerRef,
        style,
    } = useDropdownPanel(
        DropdownPosition.Below,
        DropdownDirection.Down,
        DropdownAlignment.AlignLeft,
        8,
        window.innerHeight * 0.5,
    );


    useEffect(() => {
        const handleClickOutside = (e) => {
            if (elementRef.current && !elementRef.current.contains(e.target)) {
                setOpen(false);
            }
        };

        if (open) {
            window.addEventListener("mousedown", handleClickOutside);
        }
        return () => window.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    const toggleMenuOpen = () => {
        setOpen(!open);
    };

    const changeValueType = (valueType) => {
        setOpen(false);
        props.ChangeValueType(valueType);
    };

    if (!props.Visible || !props.ValueTypes || props.ValueTypes.length === 0) {
        if (props.ValueType === MultiValueInputTypes.Select || props.ValueType === MultiValueInputTypes.Text) {
            return null;
        }
        return <div className="eone-ui-multi-value-type-input-menu-no-icon" />;
    }

    if (props.ValueTypes.length === 1) {
        return (
            <div className="eone-ui-multi-value-type-input-menu-icon" title={props.SelectedValueType.Title}>
                <FontAwesomeIcon fixedWidth={true} icon={props.SelectedValueType.Icon} />
            </div>
        )
    }

    return (
        <div className="eone-ui-multi-value-type-input-menu-button-container" ref={triggerRef}>
            <button type="button" className="eone-ui-multi-value-type-input-menu-button" title={props.SelectedValueType.Label} onClick={toggleMenuOpen}>
                <FontAwesomeIcon fixedWidth={true} icon={props.SelectedValueType.Icon} data-testid={getDataTestId(props.DataTestId, "menu")} />
            </button>
            <DropdownPanel DropdownPanelRef={elementRef} Style={style} Open={open} Close={close} >
                <ValueTypeMenu
                    Open={open}
                    ValueTypes={props.ValueTypes}
                    ChangeValueType={changeValueType} />
            </DropdownPanel>
        </div>
    )
}

function MultiValueTypeInput(props) {
    const datePickerTriggerRef = useRef(null);

    const changeValueType = (valueType) => {
        props.ChangeValueType(valueType, props.Data);
    };

    const changeValue = (value) => {
        props.ChangeValue(value, props.Data);
    };

    const updateValue = (value) => {
        if (typeof props.UpdateValue === "function") {
            props.UpdateValue(value, props.Data);
        }
        else if (typeof props.ChangeValue === "function") {
            props.ChangeValue(value, props.Data);
        }
    };

    const changeAndBlurValue = (value) => {
        changeValue(value);
        updateValue(value);
    };

    const showMenuButton = () => {
        if (props.ReadOnly) return false;
        if (props.HideValueTypeButton) return false;
        if (props.ValueTypes.length <= 1) return false;
        return true;
    };

    const selectedValueType = props.ValueTypes.find(item => item.ValueType === props.ValueType);
    if (!selectedValueType) {
        return null;
    }

    /*
        props.AllowMultiple lets you multi select per value, needed for filter modal variables where 
        you can have multiple variables but only some of them have AllowMultiple property.
    */
    const multiSelect = selectedValueType.MultiSelect || props.AllowMultiple;

    return (
        <div className={props.ContainerClass || "eone-ui-multi-value-type-input-container"} ref={datePickerTriggerRef}>
            <ValueTypeMenuButton
                Visible={showMenuButton()}
                ValueType={selectedValueType.Type}
                ValueTypes={props.ValueTypes}
                SelectedValueType={selectedValueType}
                ChangeValueType={changeValueType} />
            <InputString
                Visible={selectedValueType.Type === MultiValueInputTypes.None}
                Placeholder={selectedValueType.Label}
                ReadOnly
                ContainerClass={props.InputContainerClass || "eone-ui-multi-value-type-input-string-container"} />
            <InputString
                Visible={selectedValueType.Type === MultiValueInputTypes.String}
                ReadOnly={props.ReadOnly}
                ContainerClass={props.InputContainerClass || "eone-ui-multi-value-type-input-string-container"}
                Placeholder={selectedValueType.Placeholder}
                MaxLength={selectedValueType.MaxLength}
                Value={props.Value}
                OnBlur={updateValue}
                OnChange={changeValue} />
            <InputText
                Visible={selectedValueType.Type === MultiValueInputTypes.Text}
                ReadOnly={props.ReadOnly}
                Class="eone-ui-multi-value-type-input-text-container"
                ContainerClass={props.InputContainerClass || "eone-ui-multi-value-type-input-text-container"}
                Placeholder={selectedValueType.Placeholder}
                MaxLength={selectedValueType.MaxLength}
                Value={props.Value}
                OnBlur={updateValue}
                OnChange={changeValue} />
            <InputNumber
                Visible={selectedValueType.Type === MultiValueInputTypes.Number}
                ReadOnly={props.ReadOnly}
                ContainerClass={props.InputContainerClass || "eone-ui-multi-value-type-input-number-container"}
                Placeholder={selectedValueType.Placeholder}
                Locale={props.Locale}
                HasDecimals={selectedValueType.HasDecimals}
                DecimalPlaces={selectedValueType.DecimalPlaces}
                AllowNegative={selectedValueType.AllowNegative}
                Value={props.Value}
                OnBlur={updateValue}
                OnChange={changeValue} />
            <InputTime
                Visible={selectedValueType.Type === MultiValueInputTypes.Time}
                ReadOnly={props.ReadOnly}
                ContainerClass={props.InputContainerClass || "eone-ui-multi-value-type-input-string-container"}
                Value={props.Value}
                OnBlur={updateValue}
                OnChange={changeValue} />
            <InputGuid
                Visible={selectedValueType.Type === MultiValueInputTypes.Guid}
                ReadOnly={props.ReadOnly}
                ContainerClass={props.InputContainerClass || "eone-ui-multi-value-type-input-string-container"}
                Value={props.Value}
                OnBlur={updateValue}
                OnChange={changeValue} />
            <InputTimeSpan
                Visible={selectedValueType.Type === MultiValueInputTypes.TimeSpan}
                ReadOnly={props.ReadOnly}
                ContainerClass={props.InputContainerClass || "eone-ui-multi-value-type-input-string-container"}
                DisplayUnit={selectedValueType.DisplayUnit}
                Value={props.Value}
                OnBlur={updateValue}
                OnChange={changeValue} />
            <Select
                Visible={selectedValueType.Type === MultiValueInputTypes.Select}
                ReadOnly={props.ReadOnly}
                ContainerClass={props.InputContainerClass || "eone-ui-multi-value-type-input-select-container"}
                Class={"eone-ui-multi-value-type-input-select-button-container"}
                Placeholder={selectedValueType.Placeholder}
                Options={selectedValueType.Options}
                MultiSelect={multiSelect}
                Search
                Value={props.Value}
                OnChange={changeAndBlurValue} />
            <DatePicker
                Visible={selectedValueType.Type === MultiValueInputTypes.Date}
                ReadOnly={props.ReadOnly}
                ContainerClass={props.InputContainerClass || "eone-ui-multi-value-type-input-date-container"}
                Locale={props.Locale}
                Value={props.Value}
                OnChange={changeAndBlurValue}
                OverrideTriggerRef={datePickerTriggerRef} />
            <TagControl
                ValueType={selectedValueType.Type}
                Visible={selectedValueType.Type === MultiValueInputTypes.Strings || selectedValueType.Type === MultiValueInputTypes.Numbers}
                ReadOnly={props.ReadOnly}
                ContainerClass={props.InputContainerClass || "eone-ui-multi-value-type-input-strings-container"}
                Locale={props.Locale}
                Value={props.Value}
                OnUpdate={updateValue}
                OnChange={changeValue} />
        </div>
    );
}

export { MultiValueInputTypes, MultiValueTypeInput };
